// crypto.js — AES-256-GCM hint encryption / decryption.
//
// Cloud mode stores path-bearing fields (subject, body, resource,
// repo, file paths) as base64url-encoded ciphertext. The coordinator
// only ever sees the encrypted form. The daemon writes them with
// `encryptString` and the dashboard decrypts them client-side with
// state.hmacKey (loaded via the Worker's /v1/teams/hmac-key when the
// user signs in — see loadHmacKeyAuto in bootstrap.js).
//
// Same envelope format as the daemon: [12-byte IV || ciphertext]
// base64url-encoded without padding.

import { state } from "./state.js";

// deriveAESKey turns the base64url team HMAC key into a non-
// exportable AES-GCM CryptoKey usable by decryptHint/encryptHint.
// The first 32 bytes of the decoded key are the AES seed; the
// daemon's primitive does the same slice on its side.
//
// Returns null on malformed input rather than throwing — callers
// (boot, /v1/team-key handler) treat null as "key not usable
// yet; stay in encrypted-fallback mode".
export async function deriveAESKey(hmacKeyB64) {
  try {
    let b64 = hmacKeyB64.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const binary = atob(b64);
    const raw = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) raw[i] = binary.charCodeAt(i);
    return await crypto.subtle.importKey(
      "raw",
      raw.slice(0, 32),
      { name: "AES-GCM" },
      false,
      ["decrypt"],
    );
  } catch (e) {
    return null;
  }
}

// decryptHeartbeat unwraps a single /v1/teams/heartbeats row into
// the dashboard's normalized shape. Splits display_hint
// ("<file>\n<tool>") into separate fields and decrypts the
// device_hint (added v0.14.5) so the chat-row renderer can show
// "user@host" instead of "?".
//
// Caller-visible shape: { agent_id, laptop_id, last_seen,
// age_seconds, lastFile, lastTool, deviceLabel }.
export async function decryptHeartbeat(h) {
  let lastFile = "";
  let lastTool = h.last_tool || "";
  if (h.display_hint) {
    const plain = await decryptHint(h.display_hint);
    if (plain != null) {
      const nl = plain.indexOf("\n");
      if (nl >= 0) {
        lastFile = plain.slice(0, nl);
        if (!lastTool) lastTool = plain.slice(nl + 1);
      } else {
        lastFile = plain;
      }
    }
  }
  let deviceLabel = "";
  if (h.device_hint) {
    const plain = await decryptHint(h.device_hint);
    if (plain != null) deviceLabel = plain;
  }
  return {
    agent_id: h.agent_id,
    laptop_id: h.laptop_id || "",
    last_seen: h.last_seen,
    age_seconds: h.age_seconds || 0,
    lastFile,
    lastTool,
    deviceLabel,
    // Durable managed-edit total (plaintext count, no path). Lets the chat
    // row show real edits after write leases expire instead of "0 edits".
    editCount: h.edit_count || 0,
    // Files the chat holds as of its last push.
    heldLeases: h.held_leases || 0,
  };
}

// loadHmacKey installs a freshly-issued team key into the runtime
// state + persists the base64 form for the next page load. Called
// from three places: the `?token=...&key=...` deep-link path
// (`agc team dashboard`), the /v1/teams/hmac-key coordinator fetch
// (loadHmacKeyAuto in bootstrap.js), and manual paste in settings.
export async function loadHmacKey(keyB64) {
  if (!keyB64) return;
  const key = await deriveAESKey(keyB64);
  if (key) {
    state.hmacKey = key;
    state.hmacKeyB64 = keyB64;
    localStorage.setItem("ac-hmac-key", keyB64);
  }
}

// decryptHint unwraps a base64url-encoded hint. Returns null if:
//   - hint is empty
//   - state.hmacKey isn't loaded (solo / not-signed-in)
//   - decoding/decrypt fails (wrong key, malformed payload)
//
// Callers should treat null as "key missing or rotated" and render
// a fallback (e.g. "(encrypted)" or an empty string).
export async function decryptHint(hint) {
  if (!hint || !state.hmacKey) return null;
  try {
    let b64 = hint.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const binary = atob(b64);
    const data = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) data[i] = binary.charCodeAt(i);
    if (data.length < 13) return null;
    const plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: data.slice(0, 12) },
      state.hmacKey,
      data.slice(12),
    );
    return new TextDecoder().decode(plain);
  } catch {
    return null;
  }
}

// encryptHint mirrors decryptHint for outbound writes (PR 4 of the
// A2A handoffs feature — dashboard-originated handoff sends). Uses
// the same AES-256-GCM crypto and base64url envelope the daemon's
// encryptString primitive uses, so the receiving daemon decrypts
// identically.
//
// Returns "" when no team HMAC key is loaded (e.g. solo daemon
// mode); callers handle that as "send unencrypted is not safe —
// refuse".
export async function encryptHint(plaintext) {
  if (!plaintext || !state.hmacKey) return "";
  try {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      state.hmacKey,
      new TextEncoder().encode(plaintext),
    );
    const ctBytes = new Uint8Array(ct);
    const out = new Uint8Array(iv.length + ctBytes.length);
    out.set(iv, 0);
    out.set(ctBytes, iv.length);
    let bin = "";
    for (let i = 0; i < out.length; i++) bin += String.fromCharCode(out[i]);
    // base64url, no padding — matches the daemon's
    // base64.RawURLEncoding output.
    return btoa(bin)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  } catch {
    return "";
  }
}
